import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Sheet,
  Typography,
} from "@mui/joy";
import { PublicWorkspaceType } from "../../common/types";
import { getModuleIcon } from "./moduleIcons";

interface PublicWorkspacesSectionProps {
  workspaces: PublicWorkspaceType[];
  error?: string | null;
  joiningWorkspaceId?: string | null;
  onJoin: (workspaceId: string) => void;
}

/** Public workspace rows use the same striped row treatment as dashboard workspaces. */
const PublicWorkspacesSection: React.FC<PublicWorkspacesSectionProps> = ({
  workspaces,
  error,
  joiningWorkspaceId = null,
  onJoin,
}) => {
  if (workspaces.length === 0 && !error) return null;

  return (
    <Card>
      <CardContent>
        <Typography level="h3" sx={{ mb: 2 }}>
          Public workspaces in your organisation
        </Typography>
        {error && (
          <Alert color="danger" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {workspaces.length > 0 && (
          <Sheet
            variant="outlined"
            sx={{ borderRadius: "md", overflow: "hidden" }}
          >
            <Box sx={{ py: 0.5 }}>
              {workspaces.map((workspace, index) => {
                const moduleId = workspace.enabled_modules[0] || "unknown";
                return (
                  <Box
                    key={workspace.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      py: 1.25,
                      px: 2,
                      borderRadius: "sm",
                      bgcolor:
                        index % 2 === 0 ? "transparent" : "background.level1",
                      transition: "background-color 0.15s ease",
                      "&:hover": { bgcolor: "background.level2" },
                    }}
                  >
                    <Box
                      aria-hidden="true"
                      data-testid={`public-workspace-module-icon-${moduleId}`}
                      sx={{ display: "flex", alignItems: "center" }}
                    >
                      {getModuleIcon(moduleId)}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography level="body-md" sx={{ fontWeight: 500 }}>
                        {workspace.name}
                      </Typography>
                      <Typography
                        level="body-sm"
                        color="neutral"
                        noWrap
                        data-testid={`public-workspace-description-${workspace.id}`}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {workspace.description || "No description provided."}
                      </Typography>
                    </Box>
                    <Button
                      size="sm"
                      onClick={() => onJoin(workspace.id)}
                      loading={joiningWorkspaceId === workspace.id}
                      disabled={joiningWorkspaceId !== null}
                    >
                      Join
                    </Button>
                  </Box>
                );
              })}
            </Box>
          </Sheet>
        )}
      </CardContent>
    </Card>
  );
};

export default PublicWorkspacesSection;
